import { el, formatNumber } from './dom';

/**
 * One leaderboard row – delivered by the server-side leaderboard (one entry per account, LeaderboardService): company
 * name and logo, then the short name (3 letters first name + 3 letters last name, ructec 2026-10-08).
 */
export interface LeaderboardEntry {
  readonly name: string;
  readonly score: number;
  /** Random id of the entry (marks the player's own row) – never an account id. */
  readonly id?: string;
  readonly company?: string;
  /** Company logo, served by the leaderboard backend only. */
  readonly logoUrl?: string | null;
  /** Metres of the best run (ranking is by points, ructec 2026-10-08). */
  readonly meters?: number | null;
}

/** The signed-in player's own place (any rank – also outside the shown list). */
export interface OwnLeaderboardEntry extends LeaderboardEntry {
  readonly rank: number;
}

/** Label of a place nobody holds yet. */
export const FREE_PLACE = 'Platz frei';

/** One rendered row: a ranked entry, a free place, or the player's own place below the list. */
export interface LeaderboardRow {
  readonly rank: number;
  readonly name: string;
  readonly score: number | null;
  readonly company: string | null;
  readonly logoUrl: string | null;
  readonly meters: number | null;
  /** The signed-in player's row (highlighted green). */
  readonly own: boolean;
  readonly free: boolean;
  /** Own place outside the list: shown after a gap with its real rank. */
  readonly belowList: boolean;
}

/**
 * Rows of a list with `size` places: the delivered entries first, the remaining places free. The player's own
 * entry is highlighted where it stands; outside the list it follows below with its rank. Never fills free places
 * with made-up names.
 */
export function leaderboardRows(entries: readonly LeaderboardEntry[], own: OwnLeaderboardEntry | null, size: number): LeaderboardRow[] {
  const isOwn = (entry: LeaderboardEntry, rank: number) => (own?.id && entry.id ? own.id === entry.id : own?.rank === rank);
  const rows: LeaderboardRow[] = Array.from({ length: size }, (_, index) => {
    const entry = entries[index];
    const rank = index + 1;
    return entry
      ? { rank, name: entry.name, score: entry.score, company: entry.company ?? null, logoUrl: entry.logoUrl ?? null, meters: entry.meters ?? null, own: isOwn(entry, rank), free: false, belowList: false }
      : { rank, name: FREE_PLACE, score: null, company: null, logoUrl: null, meters: null, own: false, free: true, belowList: false };
  });
  if (own && own.rank > size && !rows.some((row) => row.own)) {
    rows.push({ rank: own.rank, name: own.name, score: own.score, company: own.company ?? null, logoUrl: own.logoUrl ?? null, meters: own.meters ?? null, own: true, free: false, belowList: true });
  }
  return rows;
}

/** Ranked list with a fixed number of places (see leaderboardRows). */
export class LeaderboardList {
  readonly element: HTMLOListElement;

  constructor(
    parent: HTMLElement,
    private readonly size: number,
    className: string,
  ) {
    this.element = el('ol', `leader-list ${className}`, undefined, parent);
    this.setEntries([], null);
  }

  setEntries(entries: readonly LeaderboardEntry[], own: OwnLeaderboardEntry | null): void {
    this.element.replaceChildren(
      ...leaderboardRows(entries, own, this.size).map((row) => {
        const classes = ['leader-row'];
        if (row.free) classes.push('is-free');
        if (row.own) classes.push('is-own');
        if (row.belowList) classes.push('is-below');
        const item = el('li', classes.join(' '));
        item.value = row.rank; // <ol> numbering for screen readers: the own place keeps its real rank
        el('span', 'leader-rank', String(row.rank), item);
        if (row.company !== null) {
          const company = el('span', 'leader-company', undefined, item);
          if (row.logoUrl) {
            const logo = el('img', 'leader-logo', undefined, company) as HTMLImageElement;
            logo.src = row.logoUrl;
            logo.alt = '';
            logo.loading = 'lazy';
            logo.addEventListener('error', () => logo.remove());
          }
          el('span', 'leader-company-name', row.company, company);
        }
        el('span', 'leader-name', row.own ? `${row.name} (du)` : row.name, item);
        if (row.meters !== null) el('span', 'leader-meter', `${formatNumber(row.meters)} m`, item);
        el('span', 'leader-score', row.score === null ? '–' : `${formatNumber(row.score)} P`, item);
        return item;
      }),
    );
  }
}
